import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { SITE } from "@/lib/site";

type UnsubStatus = "ok" | "already" | "invalid" | "unknown";

export const Route = createFileRoute("/unsubscribe")({
  validateSearch: (s: Record<string, unknown>): { status: UnsubStatus } => {
    const raw = typeof s.status === "string" ? s.status : "";
    const status: UnsubStatus =
      raw === "ok" || raw === "already" || raw === "invalid" ? raw : "unknown";
    return { status };
  },
  head: () => ({
    meta: [
      { title: `Unsubscribed — ${SITE.name}` },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: UnsubscribePage,
});

function UnsubscribePage() {
  const { status } = Route.useSearch();
  const copy = COPY[status as UnsubStatus] ?? COPY.unknown;
  return (
    <div className="min-h-dvh bg-background">
      <SiteHeader />
      <main id="main" className="mx-auto max-w-2xl px-5 sm:px-8 py-24 text-center">
        <p className="eyebrow">Subscription</p>
        <h1 className="headline-display text-4xl mt-4">{copy.title}</h1>
        <p className="mt-6 text-lg text-muted-foreground">{copy.body}</p>
        <div className="mt-10 flex gap-3 justify-center">
          <Link to="/" className="bg-foreground text-background px-5 py-3 text-sm font-semibold hover:bg-ochre-deep transition-colors">
            Back to Map Observer
          </Link>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}

const COPY: Record<UnsubStatus, { title: string; body: string }> = {
  ok: {
    title: "You've been unsubscribed.",
    body: "You will no longer receive the Friday Brief. If this was a mistake, you can resubscribe from the homepage.",
  },
  already: {
    title: "Already unsubscribed.",
    body: "This address is not on our list. Nothing further to do.",
  },
  invalid: {
    title: "That link isn't valid.",
    body: "The unsubscribe link may have expired or been mistyped. Reply to any brief and we'll remove you manually.",
  },
  unknown: {
    title: "Manage your subscription",
    body: "Use the unsubscribe link at the bottom of any Friday Brief to remove yourself instantly.",
  },
};
